/*
XOL-653 — Settings -> Finance -> "Bulk licensee rate change": the filter
contract with xolas-tms-api, in ONE place.

The names below are the contract in the API plan's section 1 (final, 2026-10-19;
no renames). They were exercised against xolas-tms-api #293 in the PR's live check. If the API
ever renames a key, this file (and the copy in the components) is the only thing
that changes. Plan: docs/plans/2026-10-19-xol-653-bulk-rate-build-plan.md, and
"Final API contract" in docs/plans/2026-10-18-xol-653-bulk-rate-site-filters.md.

The same plain keys serve BOTH endpoints:
  - GET  /licensees/search           as `site_id[]=…` (axios 0.15.3 appends the brackets)
  - POST /licensees/bulk-rate        as JSON arrays inside `filter`
That is what keeps "Apply to all N matching" the same set as the table the user
searched. The API pins that with a test; the SPA cannot detect a mismatch.

Pure module, no Vue. ES5-safe: no `?.`, no `??`, no object spread (Babel 6 build).
------------------------------------------------------------------------ */

import { isAbsent } from '@/lib/licenseeDates'

// filter key names.
export var FILTER_KEYS = {
  siteId: 'site_id',
  city: 'city',
  state: 'state',
  district: 'district',
  siteGroup: 'site_group',
  rateStatus: 'rate_status',
  expiryFrom: 'expiry_from',
  expiryTo: 'expiry_to',
  noExpiry: 'no_expiry',
  overrideMin: 'override_min',
  overrideMax: 'override_max'
}

// rate_status values. `has` = "has a resolvable sharer rate":
// overridden licensees with no rate are NOT included (Carlos, 2026-10-19).
export var RATE_STATUS = { ANY: 'any', HAS: 'has', NONE: 'none' }

// the value that means "sites with no site group".
export var NO_GROUP = '__none__'

// Client-only: the disabled "Showing the first 30" row. Never sent.
export var CAP_VALUE = '__cap__'

// telco-scoped, super-only suggestions endpoint.
export var SUGGEST_PATH = '/licensees/search/suggest'

// Part B — one atomic flag on PUT /licensees/{id} and on
// bulk-rate mode=rate. The rate write and the unlock happen in one transaction.
export var CLEAR_OVERRIDE_KEY = 'clear_override'

// The bulk-rate 202 reports `overridesToClear` (int): a PROJECTION of the locked
// targets in the rate's org, counted when the request was resolved (0 when the flag
// is off). It is not a count of overrides already cleared.
export var OVERRIDES_TO_CLEAR_KEY = 'overridesToClear'

// The five site pickers. `api` doubles as the `field` argument of the suggest
// endpoint. Remote fields search as the user types; list fields load the telco's
// full distinct list when the picker opens and filter on the client.
export var SITE_FIELDS = {
  siteId: { api: FILTER_KEYS.siteId, remote: true, minChars: 3, limit: 30 },
  city: { api: FILTER_KEYS.city },
  state: { api: FILTER_KEYS.state },
  district: { api: FILTER_KEYS.district },
  siteGroup: { api: FILTER_KEYS.siteGroup, noGroup: true }
}

// Same absent-date rule the licensee screens use (null, '' and '0000-00-00').
export var isNoExpiry = isAbsent

function isNumber (v) {
  return typeof v === 'number' && isFinite(v)
}

function stripCap (list) {
  var out = []
  for (var i = 0; i < list.length; i++) {
    if (list[i] !== CAP_VALUE) out.push(list[i])
  }
  return out
}

/**
 * Serialise the filter form into request params. Pure: returns a new object
 * with new arrays, and never reads `this`.
 *
 * @param {Object} s - the form state: telcoId, structureTypes, heightMin,
 *   heightMax, handoverRange, startedRange, filterRateId, filterVersionId,
 *   rateOverridden, overrideMin, overrideMax, sitePicks, rateStatus,
 *   expiryRange, noExpiry.
 * @returns {Object}
 */
export function buildSearchParams (s) {
  var params = {}
  if (s.telcoId) params.organisation_id = s.telcoId

  // Plain key — axios 0.15.3 auto-appends `[]` to array values, producing
  // `structure_type[]=a&structure_type[]=b` (Laravel reads it as an array).
  // Using the bracketed key here would double-bracket → `structure_type[][]=`.
  if (s.structureTypes && s.structureTypes.length) {
    params.structure_type = s.structureTypes.slice()
  }

  // Truthy check: el-input-number coerces an empty value to 0, and a 0 height
  // bound is meaningless (it would filter every structure out) — so omit 0/null.
  if (s.heightMin) params.height_min = s.heightMin
  if (s.heightMax) params.height_max = s.heightMax

  if (s.handoverRange && s.handoverRange[0]) params.handover_from = s.handoverRange[0]
  if (s.handoverRange && s.handoverRange[1]) params.handover_to = s.handoverRange[1]
  if (s.startedRange && s.startedRange[0]) params.started_from = s.startedRange[0]
  if (s.startedRange && s.startedRange[1]) params.started_to = s.startedRange[1]

  // Rate/version filters.
  if (s.filterRateId) params.rate_id = s.filterRateId
  if (s.filterVersionId) params.rate_version_id = s.filterVersionId

  // Override-set filter: checked → only manually-overridden licensees
  // (API `rate_locked=1` → licensees.rateLocked = 1). Unchecked → omit (both).
  if (s.rateOverridden) params.rate_locked = 1

  // Override amount range. Guard (serialiser layer 3): only meaningful — and
  // only sent — while Rate overridden is set, so a stale amount typed before
  // the box was unticked can never narrow the search. 0 IS a valid bound (a
  // fee override of 0), unlike the height bounds above, so test for a number.
  if (s.rateOverridden) {
    if (isNumber(s.overrideMin)) params[FILTER_KEYS.overrideMin] = s.overrideMin
    if (isNumber(s.overrideMax)) params[FILTER_KEYS.overrideMax] = s.overrideMax
  }

  // Rate status. Guard (serialiser layer 2): a specific Current sharer rate
  // wins, so a stale `has`/`none` is never sent with `rate_id`.
  if (!s.filterRateId && (s.rateStatus === RATE_STATUS.HAS || s.rateStatus === RATE_STATUS.NONE)) {
    params[FILTER_KEYS.rateStatus] = s.rateStatus
  }

  // Site picks: plain keys, omitted when empty, new arrays, CAP_VALUE stripped.
  var picks = s.sitePicks || {}
  Object.keys(SITE_FIELDS).forEach(function (uiKey) {
    var list = picks[uiKey]
    if (!list || !list.length) return
    var clean = stripCap(list)
    if (clean.length) params[SITE_FIELDS[uiKey].api] = clean
  })

  // Expiry. Guard: No expiry sends ONLY `no_expiry` — never the bounds — so a
  // range left in the form cannot turn "no expiry" into an empty set.
  if (s.noExpiry) {
    params[FILTER_KEYS.noExpiry] = 1
  } else {
    if (s.expiryRange && s.expiryRange[0]) params[FILTER_KEYS.expiryFrom] = s.expiryRange[0]
    if (s.expiryRange && s.expiryRange[1]) params[FILTER_KEYS.expiryTo] = s.expiryRange[1]
  }

  return params
}

/**
 * Shallow copy with the arrays sliced. The snapshot taken at Search must not
 * share array references with the live form.
 */
export function cloneParams (p) {
  var out = {}
  var src = p || {}
  Object.keys(src).forEach(function (k) {
    out[k] = Array.isArray(src[k]) ? src[k].slice() : src[k]
  })
  return out
}

/**
 * URL for one picker's suggestions.
 *
 * @param {Number|String} orgId
 * @param {String} uiKey - a SITE_FIELDS key.
 * @param {String} [q] - the typed query; only remote fields send it.
 */
export function suggestUrl (orgId, uiKey, q) {
  var f = SITE_FIELDS[uiKey]
  var url = SUGGEST_PATH + '?organisation_id=' + encodeURIComponent(orgId) +
    '&field=' + encodeURIComponent(f.api)
  if (f.remote && q) url += '&q=' + encodeURIComponent(q)
  // One extra row lets the SPA tell "exactly the limit" from "more than the limit".
  if (f.limit) url += '&limit=' + (f.limit + 1)
  return url
}

/**
 * Map the suggest response to picker rows.
 *
 * @param {String} uiKey
 * @param {Array|Object} data - a bare array or `{data: […]}`.
 * @returns {Array<{value, label, count, siteName, telcoSiteId, cap}>}
 */
export function normaliseSuggestions (uiKey, data) {
  var f = SITE_FIELDS[uiKey]
  var raw = Array.isArray(data) ? data : (data && Array.isArray(data.data) ? data.data : [])
  var rows = []
  for (var i = 0; i < raw.length; i++) {
    var r = raw[i]
    if (!r || r.value === null || r.value === undefined || r.value === '') continue
    rows.push({
      value: r.value,
      label: r.label !== null && r.label !== undefined && r.label !== '' ? String(r.label) : String(r.value),
      count: r.count,
      siteName: r.siteName,
      telcoSiteId: r.telcoSiteId
    })
  }

  if (f.noGroup) {
    var front = []
    var rest = []
    rows.forEach(function (r) {
      if (r.value === NO_GROUP) {
        r.label = 'No group'
        front.push(r)
      } else {
        rest.push(r)
      }
    })
    rows = front.concat(rest)
  }

  if (f.limit && rows.length > f.limit) {
    rows = rows.slice(0, f.limit)
    rows.push({ value: CAP_VALUE, label: '', cap: true })
  }

  return rows
}

/**
 * Params for the "how many of the matching licensees have a rate override"
 * pre-flight: the same filter, restricted to overridden rows, one row per
 * page — only `total` is read. Does not mutate its input.
 */
export function lockedCountParams (filter) {
  var out = cloneParams(filter)
  out.rate_locked = 1
  out.page = 1
  out.per_page = 1
  return out
}
